import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useAction, useGlobalAction, useSession } from '@gadgetinc/react';
import { api } from '../api';
import { speakText, playMp3Bytes, initializeAudio, playDispatchTones, startSubaudibleTone, stopSubaudibleTone, playSubaudibleToneBurst } from '../lib/audioUtils';
import { generateDeviceFingerprint } from '../lib/deviceFingerprint';
import { clientLogger } from '../lib/clientLogger';
import VoiceSimulationFlowChart from './VoiceSimulationFlowChart';
import ManualPhaseAdvanceButton from './ManualPhaseAdvanceButton';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Spinner,
  Divider,
  useToast,
  Badge,
  Container,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  Textarea,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  Icon,
  IconButton,
  Avatar,
  Fade,
  ScaleFade,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';

const VoiceScenarioRunner = ({ scenarioId, voiceSimSession, onComplete }) => {
  const session = useSession(api);
  
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState('idle');
  const [assistantText, setAssistantText] = useState('');
  const [userText, setUserText] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [runId] = useState(() => 'run_' + crypto.randomUUID());
  const [transcript, setTranscript] = useState([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlayingOpener, setIsPlayingOpener] = useState(false);
  const [pendingArrivalSegment, setPendingArrivalSegment] = useState(null);
  const [pendingSegment2, setPendingSegment2] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [inputMode, setInputMode] = useState('voice'); // 'voice' or 'text'
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [debriefReady, setDebriefReady] = useState(false);
  const [debriefData, setDebriefData] = useState(null);
  const [isPlayingDebrief, setIsPlayingDebrief] = useState(false);
  const [pagerToneVolume, setPagerToneVolume] = useState(1.0); // 100% default volume (system volume)
  const [speechSpeed, setSpeechSpeed] = useState(() => {
    // Load from localStorage or default to 0 (which maps to 1.25x actual speed)
    const saved = localStorage.getItem('voiceSpeechSpeed');
    return saved ? parseFloat(saved) : 0;
  });
  const [checkInCount, setCheckInCount] = useState(0);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('DISPATCH');
  const [sessionIdForManualAdvance, setSessionIdForManualAdvance] = useState(null);
  const [phaseTransition, setPhaseTransition] = useState(null); // For visual feedback
  const [isPreloadingScene, setIsPreloadingScene] = useState(false);
  const [subaudibleToneActive, setSubaudibleToneActive] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [activeSessionData, setActiveSessionData] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const initializingRef = useRef(false);
  const pagerTonesRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastSubmissionRef = useRef({ text: '', timestamp: 0 });
  const subaudibleToneRef = useRef(null);
  const unlockedAudioRef = useRef(null);
  const dispatchTriggeredRef = useRef(false);
  const textInputRef = useRef(null);

  const toast = useToast();
  const { isOpen: isRecoveryModalOpen, onOpen: onRecoveryModalOpen, onClose: onRecoveryModalClose } = useDisclosure();
  const { isOpen: isEndModalOpen, onOpen: onEndModalOpen, onClose: onEndModalClose } = useDisclosure();
  
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState(null);

  // Global action hooks
  const [{ data: checkSessionData, fetching: checkingSession }, checkActiveSession] = useGlobalAction(api.checkActiveVoiceSession);
  const [{ data: endSessionData, fetching: endingSession }, endSession] = useGlobalAction(api.endVoiceSession);
  const [{ fetching: updatingSession }, updateSession] = useAction(api.simulationSession.update);

  // Helper function to get badge styling for speaker roles
  const getSpeakerBadge = (role) => {
    // Normalize the role string
    const normalizedRole = (role || 'preceptor').toLowerCase().trim();
    
    const badgeConfig = {
      dispatcher: { color: 'red', label: 'Dispatch', emoji: '📻' },
      dispatch: { color: 'red', label: 'Dispatch', emoji: '📻' },
      preceptor: { color: 'purple', label: 'Preceptor', emoji: '👨‍⚕️' },
      partner: { color: 'blue', label: 'Partner', emoji: '👨‍⚕️' },
      patient: { color: 'orange', label: 'Patient', emoji: '🤒' },
      nurse: { color: 'teal', label: 'Nurse', emoji: '👩‍⚕️' },
      school_nurse: { color: 'teal', label: 'School Nurse', emoji: '👩‍⚕️' },
      mother: { color: 'pink', label: 'Mother', emoji: '👩' },
      father: { color: 'cyan', label: 'Father', emoji: '👨' },
      hospital_nurse: { color: 'green', label: 'Hospital Nurse', emoji: '👩‍⚕️' },
      'medical control': { color: 'blue', label: 'Medical Control', emoji: '👨‍⚕️' },
      medical_control: { color: 'blue', label: 'Medical Control', emoji: '👨‍⚕️' },
      bystander: { color: 'gray', label: 'Bystander', emoji: '👤' },
      witness: { color: 'gray', label: 'Witness', emoji: '👤' }
    };

    // If we have a known config, use it
    if (badgeConfig[normalizedRole]) {
      return badgeConfig[normalizedRole];
    }
    
    // For unknown speakers, create a badge based on the role text
    // Capitalize first letter of each word
    const displayLabel = role
      .split(/[_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // Use generic color scheme for unknown speakers
    return { 
      color: 'purple', 
      label: displayLabel, 
      emoji: '👤' 
    };
  };

  // Parse response to detect multiple speakers (format: **Speaker**: text)
  const parseSpeakers = (text) => {
    if (!text) return [{ speaker: 'preceptor', text: '' }];

    // Check if response has **Speaker**: format
    const speakerPattern = /\*\*([^*]+)\*\*:\s*/g;
    const matches = [...text.matchAll(speakerPattern)];

    if (matches.length === 0) {
      // No explicit speaker markers - return as single message
      return [{ speaker: 'preceptor', text }];
    }

    // Split by speakers
    const speakers = [];
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const speaker = match[1].toLowerCase().trim();
      const startIdx = match.index + match[0].length;
      const endIdx = i < matches.length - 1 ? matches[i + 1].index : text.length;
      const speakerText = text.substring(startIdx, endIdx).trim();

      speakers.push({ speaker, text: speakerText });
    }

    return speakers;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (subaudibleToneRef.current) {
        stopSubaudibleTone(subaudibleToneRef.current);
      }
    };
  }, []);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // Initialize simulation session on mount
  useEffect(() => {
    const initSession = async () => {
      // Guard against double-init in React StrictMode
      if (initializingRef.current) {
        return;
      }
      
      try {
        initializingRef.current = true;
        setIsInitializing(true);
        
        // Generate device fingerprint for this session
        const fingerprint = await generateDeviceFingerprint();
        setDeviceFingerprint(fingerprint);
        
        // Check for active session first if we have a voiceSimSession
        if (voiceSimSession?.id) {
          try {
            const activeSessionCheck = await api.checkActiveVoiceSession({ 
              voiceSimSessionId: voiceSimSession.id,
              deviceFingerprint: fingerprint 
            });
            
            if (activeSessionCheck.hasActiveSession && activeSessionCheck.canResume) {
              // Found an active session - offer recovery
              setActiveSessionData(activeSessionCheck);
              setAttemptsRemaining(activeSessionCheck.attemptsRemaining);
              setIsRecovering(true);
              onRecoveryModalOpen();
              setIsInitializing(false);
              return;
            }
          } catch (checkError) {
            console.warn('Error checking for active session:', checkError);
            // Continue with normal initialization
          }
        }
        
        // Check if session already exists
        const existingSessions = await api.simulationSession.findMany({
          filter: { sessionId: { equals: runId } },
          select: { id: true },
          first: 1
        });

        // If session exists, we're done
        if (existingSessions && existingSessions.length > 0) {
          setSessionIdForManualAdvance(existingSessions[0].id);
          setIsInitializing(false);
          return;
        }
        
        // Prepare session data
        const sessionData = {
          sessionId: runId,
          scenario: { _link: scenarioId },
          status: 'active',
          isVoiceSession: true,
          sessionType: 'immersive',
          startedAt: new Date().toISOString(),
          deviceFingerprint: fingerprint,
          deviceBoundAt: new Date().toISOString(),
          accessedFromIPs: [],
          currentState: {
            started: false,
            phase: 'DISPATCH',
            timelineMin: 0,
            interventions: [],
            discoveredHistory: [],
            discoveredExam: []
          }
        };
        
        // If voiceSimSession is provided (tokenized access), link to that
        if (voiceSimSession?.id) {
          sessionData.voiceSimSession = { _link: voiceSimSession.id };
          
          // Use the validated voiceSimSession object directly
          if (voiceSimSession.sharedBy?.id) {
            sessionData.instructor = { _link: voiceSimSession.sharedBy.id };
          }
          
          if (voiceSimSession.cohort?.id) {
            sessionData.cohort = { _link: voiceSimSession.cohort.id };
          }
        }
        // Otherwise, this is a logged-in instructor, get their user ID
        else {
          if (session?.user?.id) {
            sessionData.user = { _link: session.user.id };
          }
        }
        
        // Create simulationSession record
        const createdSession = await api.simulationSession.create(sessionData);
        setSessionIdForManualAdvance(createdSession.id);

        setIsInitializing(false);
      } catch (error) {
        console.error('Failed to initialize session:', error);
        
        // If it's a duplicate error, just continue (React StrictMode double-mount)
        if (error.message?.includes('not unique')) {
          console.log('Session already exists (duplicate from StrictMode), continuing...');
          setIsInitializing(false);
          return;
        }
        
        setInitError(error.message);
        setIsInitializing(false);
        
        toast({
          title: 'Initialization Error',
          description: 'Failed to initialize voice scenario session.',
          status: 'error',
          duration: 10000,
          isClosable: true,
        });
      }
    };
    
    initSession();
  }, [scenarioId, runId, voiceSimSession, session, toast]);

  // Recover session state on reload
  useEffect(() => {
    const recoverSession = () => {
      try {
        const savedStateKey = `voiceScenario_${runId}`;
        const savedState = localStorage.getItem(savedStateKey);
        
        if (savedState) {
          const state = JSON.parse(savedState);
          console.log('Recovering session state:', state);
          
          // Only restore lightweight state, not audio segments
          if (state.conversationId) setConversationId(state.conversationId);
          
          // Ensure transcript is always an array
          if (state.transcript && Array.isArray(state.transcript)) {
            setTranscript(state.transcript);
          } else {
            console.warn('Invalid transcript in saved state, ignoring');
            setTranscript([]);
          }
          
          if (state.hasStarted) setHasStarted(state.hasStarted);
          // Don't restore pendingArrivalSegment - it contains large audio data
          
          toast({
            title: 'Session Recovered',
            description: 'Your scenario session has been restored.',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error('Failed to recover session:', error);
        // Reset to safe defaults on error
        setTranscript([]);
        setConversationId('');
        setHasStarted(false);
      }
    };
    
    if (!isInitializing) {
      recoverSession();
    }
  }, [isInitializing, runId, toast]);

  // Save session state periodically to database
  useEffect(() => {
    const saveSession = async () => {
      try {
        // Save to localStorage for quick recovery
        const savedStateKey = `voiceScenario_${runId}`;
        const state = {
          conversationId,
          transcript,
          hasStarted,
          timestamp: Date.now()
        };
        localStorage.setItem(savedStateKey, JSON.stringify(state));
        
        // Save to database if we have a session ID
        if (sessionIdForManualAdvance && transcript.length > 0) {
          await updateSession({
            id: sessionIdForManualAdvance,
            conversationTranscript: transcript,
            conversationId: conversationId || undefined
          });
        }
      } catch (error) {
        console.error('Failed to save session state:', error);
      }
    };
    
    if (hasStarted) {
      // Debounced save - only save every 30 seconds or after each turn
      const timeoutId = setTimeout(() => {
        saveSession();
      }, 30000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [runId, conversationId, transcript, hasStarted, sessionIdForManualAdvance, updateSession]);

  // Persist speech speed to localStorage
  useEffect(() => {
    localStorage.setItem('voiceSpeechSpeed', speechSpeed.toString());
  }, [speechSpeed]);

  // Cleanup old sessions from localStorage (keep only last 5)
  useEffect(() => {
    return () => {
      try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('voiceScenario_'));
        if (keys.length > 5) {
          // Sort by timestamp and remove oldest
          const sessions = keys.map(key => {
            try {
              const data = JSON.parse(localStorage.getItem(key));
              return { key, timestamp: data.timestamp || 0 };
            } catch {
              return { key, timestamp: 0 };
            }
          });
          
          sessions.sort((a, b) => b.timestamp - a.timestamp);
          
          // Remove all but the 5 most recent
          sessions.slice(5).forEach(session => {
            localStorage.removeItem(session.key);
          });
        }
      } catch (error) {
        console.error('Failed to cleanup old sessions:', error);
      }
    };
  }, []);

  // Auto-focus text input when switching to text mode
  useEffect(() => {
    if (inputMode === 'text' && textInputRef.current) {
      // Small delay to ensure the textarea is rendered
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 100);
    }
  }, [inputMode]);

  // Add spacebar PTT support
  useEffect(() => {
    // Only attach keyboard listeners if in voice mode and scenario has started
    if (inputMode !== 'voice' || !hasStarted) return;
    
    const handleKeyDown = (e) => {
      // Only trigger on spacebar, and prevent if already recording or not idle
      if (e.code === 'Space' && !isRecording && status === 'idle') {
        e.preventDefault(); // Prevent page scroll
        startRecording();
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.code === 'Space' && isRecording) {
        e.preventDefault();
        stopRecording();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [inputMode, hasStarted, isRecording, status]);

  // Poll for debrief when session ends
  useEffect(() => {
    if (!sessionEnded || debriefReady || !runId) return;

    // Also trigger POST to generate debrief (fallback in case server-side trigger didn't fire)
    const triggerDebrief = async () => {
      try {
        await fetch('/scenario/debrief', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ runId })
        });
      } catch (e) {
        console.error('Failed to trigger debrief generation:', e);
      }
    };
    triggerDebrief();

    // Poll every 3 seconds for up to 2 minutes
    let pollCount = 0;
    const maxPolls = 40;

    const intervalId = setInterval(async () => {
      pollCount++;
      const ready = await pollForDebrief();
      if (ready || pollCount >= maxPolls) {
        clearInterval(intervalId);
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [sessionEnded, debriefReady, runId]);

  const startRecording = async () => {
    // Play tone burst to keep audio context active
    if (subaudibleToneRef.current?.audioContext) {
      playSubaudibleToneBurst(subaudibleToneRef.current.audioContext);
    }
    
    // Note: unlockedAudioRef.current was already created in playOpenerSequence
    
    try {
      setStatus('requesting mic');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
        await sendTurn(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setStatus('recording');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: 'Microphone Error',
        description: 'Failed to access microphone. Please check permissions.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setStatus('idle');
    }
  };

  const stopRecording = () => {
    // Play tone burst to keep audio context active
    if (subaudibleToneRef.current?.audioContext) {
      playSubaudibleToneBurst(subaudibleToneRef.current.audioContext);
    }
    
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setStatus('stopping');
      
      // Stop all tracks
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Unified helper for sending voice turns (with audio, text, or check-in)
  const sendVoiceTurnWithText = async (text, isCheckIn = false, audioBlob = null) => {
    if (sessionEnded) return;

    // Prevent duplicate submissions (within 500ms of same text)
    const now = Date.now();
    if (!audioBlob && text === lastSubmissionRef.current.text &&
        now - lastSubmissionRef.current.timestamp < 500) {
      console.log('Duplicate submission detected, ignoring');
      return;
    }
    lastSubmissionRef.current = { text, timestamp: now };

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      setStatus(audioBlob ? 'uploading' : 'processing');

      const form = new FormData();
      if (audioBlob) {
        form.append('audio', audioBlob, 'turn.webm');
      } else if (text || isCheckIn) {
        form.append('text', text);
      }
      form.append('runId', runId);
      form.append('conversationId', conversationId);
      form.append('scenarioId', scenarioId);
      // Convert display speed (-0.5 to +0.5) to actual TTS speed (0.75 to 1.75)
      // Display 0 = actual 1.25x (our "normal" which is already 25% faster)
      const actualSpeed = 1.25 + speechSpeed;
      form.append('speed', actualSpeed.toString());
      if (isCheckIn) {
        form.append('checkIn', 'true');
      }

      // Add placeholder ONLY if user actually provided input (not auto-triggered system prompts)
      // For audio: audioBlob exists. For text: text is not empty.
      const userProvidedInput = audioBlob || (text && text.trim() !== '');

      if (!isCheckIn && userProvidedInput) {
        setTranscript(prev => [...prev, {
          user: text || '(Processing...)',
          assistant: '',
          isPending: true // Mark as pending so we can replace it
        }]);
      }

      const response = await fetch('/scenario/voice-turn', {
        method: 'POST',
        body: form
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('Received response data:', {
        userText: data.userText,
        assistantText: data.assistantText,
        role: data.role,
        speakerName: data.speakerName,
        ttsVoice: data.ttsVoice,
        phase: data.phase,
        currentPhase: currentPhase
      });

      setUserText(data.userText || '');
      setAssistantText(data.assistantText || '');
      setConversationId(data.conversationId || '');

      let endedByResponse = false;

      // Update current phase if provided and show transition
      if (data.phase && data.phase !== currentPhase) {
        const phaseLabels = {
          'DISPATCH': 'Dispatch',
          'EN_ROUTE': 'En Route',
          'ON_SCENE': 'On Scene',
          'LOADED': 'Patient Loaded',
          'TRANSPORTING': 'Transporting',
          'RADIO_REPORT': 'Radio Report',
          'END': 'Scenario Complete'
        };

        setPhaseTransition(phaseLabels[data.phase] || data.phase);
        setCurrentPhase(data.phase);

        // Clear transition after animation
        setTimeout(() => setPhaseTransition(null), 3000);

        // Check if END phase - trigger debrief
        if (data.phase === 'END') {
          endedByResponse = true;
          setSessionEnded(true);
          toast({
            title: 'Scenario Complete',
            description: 'Loading debrief...',
            status: 'success',
            duration: 3000
          });
        }

        // Preload scene arrival when entering ON_SCENE
        if (data.phase === 'ON_SCENE' && !isPreloadingScene) {
          setIsPreloadingScene(true);
        }
      }

      // Check if session should end
      if (data.next && data.next.type === 'end_session') {
        endedByResponse = true;
        setSessionEnded(true);
        setStatus('idle');
        toast({
          title: 'Session Ended',
          description: 'The session has ended due to inactivity.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Handle evaluation_prompt directive (scenario complete)
      if (data.next && data.next.type === 'evaluation_prompt') {
        endedByResponse = true;
        setSessionEnded(true);
        toast({
          title: 'Scenario Complete',
          description: 'Your evaluation is being prepared...',
          status: 'success',
          duration: 5000
        });
        // Don't auto-trigger another turn - scenario is over
        // Still update transcript below, then return before auto-prompt logic
      }

      // Check if we should auto-trigger phase-specific prompts
      const shouldAutoPrompt = !endedByResponse && data.next && (
        data.next.type === 'en_route_prompt' ||
        data.next.type === 'on_scene_arrival' ||
        data.next.type === 'en_route_transition'
      );

      // Update transcript
      if (isCheckIn) {
        // Check-in: just add assistant response
        setTranscript(prev => [...prev, {
          user: '',
          assistant: data.assistantText || '',
          role: data.role || 'preceptor'
        }]);
        setCheckInCount(prev => prev + 1);
      } else {
        // Normal turn: handle user input and assistant response
        setTranscript(prev => {
          const newTranscript = [...prev];
          const lastIndex = newTranscript.length - 1;

          // Check if this is an auto-triggered prompt (no user input)
          const hasUserInput = data.userText && data.userText.trim() !== '';

          // Check if last entry is our pending placeholder
          const hasPendingEntry = lastIndex >= 0 && newTranscript[lastIndex].isPending;

          if (hasPendingEntry) {
            // Replace the pending placeholder with actual data
            newTranscript[lastIndex] = {
              user: data.userText || text || '',
              assistant: data.assistantText || '',
              role: data.role || 'preceptor'
            };
          } else if (hasUserInput) {
            // No pending entry but user spoke - add new entry
            newTranscript.push({
              user: data.userText,
              assistant: data.assistantText || '',
              role: data.role || 'preceptor'
            });
          } else {
            // Auto-triggered prompt - only show assistant response
            newTranscript.push({
              user: '',
              assistant: data.assistantText || '',
              role: data.role || 'preceptor'
            });
          }
          return newTranscript;
        });
        // Reset check-in count on successful turn
        setCheckInCount(0);
      }

      // Play audio response
      if (data.audioBase64) {
        setStatus('processing');
        try {
          // Decode base64 audio and play it directly (already generated with correct voice)
          const binaryString = atob(data.audioBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Use the pre-unlocked audio element if available
          if (unlockedAudioRef.current) {
            const audio = unlockedAudioRef.current;
            
            // CRITICAL: Re-awaken the element before loading new audio (iOS fix)
            // Play a tiny bit of silence in the background - DON'T AWAIT IT
            audio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
            audio.play().then(() => {
              // Immediately pause after play starts
              audio.pause();
            }).catch(err => {
              console.warn('Keepalive play failed, continuing anyway:', err);
            });
            
            // Don't wait for keepalive, continue immediately
            await new Promise(resolve => setTimeout(resolve, 10)); // Just 10ms delay
            
            // Convert to data URL
            const base64Audio = btoa(
              new Uint8Array(bytes).reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            const dataUrl = `data:audio/mpeg;base64,${base64Audio}`;
            
            // Load into already-unlocked element
            audio.src = dataUrl;
            
            // Play (should work now)
            await audio.play();
            
            // Wait for completion
            await new Promise((resolve, reject) => {
              audio.onended = resolve;
              audio.onerror = reject;
            });
            
            clientLogger.audioDebug('assistant_audio_played_with_unlocked_element', {});
          } else {
            // Fallback to playMp3Bytes
            clientLogger.audioDebug('playing_assistant_audio', {
              audioSize: bytes.buffer.byteLength
            });
            await playMp3Bytes(bytes.buffer);
          }
        } catch (error) {
          console.error('Failed to play audio:', error);
          clientLogger.error('Assistant audio playback failed', {
            error: error.message,
            errorName: error.name
          });
          toast({
            title: 'Audio Playback Error',
            description: 'Failed to play audio response. You can read the text in the transcript.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      }

      setStatus('idle');

      // Re-focus text input after each turn completes in text mode
      if (inputMode === 'text' && textInputRef.current) {
        setTimeout(() => textInputRef.current?.focus(), 150);
      }

      // Auto-trigger phase-specific prompts (EN_ROUTE, ON_SCENE arrival, etc.)
      if (shouldAutoPrompt) {
        console.log(`Auto-triggering ${data.next.type} prompt`);
        // Brief delay to let audio finish and feel natural
        setTimeout(() => {
          sendVoiceTurnWithText('', false); // Empty text, not check-in
        }, 500);
        return; // Don't start timeout yet
      }

      // Start timeout for next input (unless session ended)
      // Text mode gets 90s (more time to type), voice mode gets 60s
      if (!sessionEnded && !endedByResponse) {
        const checkInDelay = inputMode === 'text' ? 90000 : 60000;
        timeoutRef.current = setTimeout(() => {
          console.log(`${checkInDelay / 1000}s timeout - triggering check-in`);
          sendVoiceTurnWithText('', true); // Empty text, is check-in
        }, checkInDelay);
      }

    } catch (error) {
      console.error('Error sending turn:', error);
      toast({
        title: 'Network Error',
        description: 'Failed to send turn. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setStatus('idle');
      // Re-focus text input on error recovery too
      if (inputMode === 'text' && textInputRef.current) {
        setTimeout(() => textInputRef.current?.focus(), 150);
      }
    }
  };

  const sendTurn = async (audioBlob) => {
    await sendVoiceTurnWithText('', false, audioBlob);
  };

  const sendTextTurn = async () => {
    if (!textInput.trim()) return;
    const text = textInput;
    setTextInput(''); // Clear input immediately
    await sendVoiceTurnWithText(text, false);
  };

  const playPagerTonesSequence = async (providedAudioContext = null) => {
    return new Promise((resolve, reject) => {
      try {
        // Use provided context, or fallback to subaudible tone context
        let audioContext = providedAudioContext;
        
        if (!audioContext && subaudibleToneRef.current?.audioContext) {
          audioContext = subaudibleToneRef.current.audioContext;
          console.log('Using existing AudioContext for pager tones');
        } else if (!audioContext) {
          // This shouldn't happen if called correctly, but fallback just in case
          console.warn('No AudioContext provided for pager tones, creating new one');
          audioContext = new (window.AudioContext || window.webkitAudioContext)();
          if (audioContext.state === 'suspended') {
            // Can't use await here - not in async function
            audioContext.resume().catch(err => {
              console.warn('Failed to resume fallback AudioContext:', err);
            });
          }
        }
        
        console.log('Pager tones using AudioContext, state:', audioContext.state);
        
        const masterGain = audioContext.createGain();
        masterGain.connect(audioContext.destination);
        masterGain.gain.value = pagerToneVolume; // Use volume from state
        
        // Define the tone sequence (frequencies in Hz)
        // Pattern: Doooo-DOOOOOOOOOOO-Doooo-dooooooooooo-BEEEEEP
        const toneSequence = [
          { freq: 800, duration: 0.8 },   // Tone 1: Doooo (low, short)
          { freq: 1000, duration: 1.8 },   // Tone 2: DOOOOOOOOOOO (higher, longer)
          { freq: 0, duration: 0.8 },   // Pause
          { freq: 800, duration: 0.8 },   // Tone 3: Doooo (same as tone 1)
          { freq: 400, duration: 1.8 },   // Tone 4: dooooooooooo (lowest, same length as tone 2)
          { freq: 0, duration: 0.8 },   // Pause
          { freq: 1750, duration: 1.8 },  // Tone 5: BEEEEEP (highest, slightly longer)
        ];
        
        let currentTime = audioContext.currentTime + 0.1; // Small delay to ensure context is ready
        
        // Play each tone in sequence
        toneSequence.forEach((tone, index) => {
          if (tone.freq === 0) {
            // Skip silent pauses, just add to timing
            currentTime += tone.duration;
            return;
          }
          
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(masterGain);
          
          oscillator.frequency.value = tone.freq;
          oscillator.type = 'sine';
          
          // Envelope: quick attack, sustain, quick release
          const attackTime = 0.01;
          const releaseTime = 0.01;
          
          gainNode.gain.setValueAtTime(0, currentTime);
          gainNode.gain.linearRampToValueAtTime(1, currentTime + attackTime);
          gainNode.gain.setValueAtTime(1, currentTime + tone.duration - releaseTime);
          gainNode.gain.linearRampToValueAtTime(0, currentTime + tone.duration);
          
          oscillator.start(currentTime);
          oscillator.stop(currentTime + tone.duration);
          
          currentTime += tone.duration;
          
          // Add small gap between tones (40ms)
          currentTime += 0.04;
        });
        
        // Calculate total duration by summing all tones
        const totalDuration = toneSequence.reduce((sum, tone) => sum + tone.duration + 0.04, 0) * 1000 + 100;
        console.log(`Pager tones scheduled to play for ${totalDuration}ms`);
        
        setTimeout(() => {
          console.log('Pager tones sequence complete');
          resolve();
        }, totalDuration);
        
      } catch (error) {
        console.error('Pager tones error:', error);
        reject(error);
      }
    });
  };

  const playOpenerSequence = async () => {
    // Guard against double-trigger from React StrictMode double-mount
    if (dispatchTriggeredRef.current) {
      console.log('Dispatch already triggered, skipping duplicate call');
      return;
    }
    dispatchTriggeredRef.current = true;

    clientLogger.audioDebug('playOpenerSequence_start', {
      hasStarted,
      isPlayingOpener
    });

    // CRITICAL: ALL audio unlocking must happen SYNCHRONOUSLY in the click handler
    // This is the ONLY way to unlock audio on iOS
    
    let audioContext = null;
    let unlockedAudio = null;
    
    try {
      // 1. SYNCHRONOUSLY create AudioContext
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      console.log('AudioContext created, state:', audioContext.state);
      clientLogger.audioDebug('audioContext_created', {
        state: audioContext.state
      });
      
      // 2. IMMEDIATELY start resuming (don't await yet) - this must happen synchronously
      const resumePromise = audioContext.state === 'suspended' 
        ? audioContext.resume() 
        : Promise.resolve();
      
      // 3. NOW create and play test oscillator (while resume is in progress)
      // CRITICAL FOR MOBILE: This forces iOS to unlock audio
      const testOsc = audioContext.createOscillator();
      const testGain = audioContext.createGain();
      testOsc.connect(testGain);
      testGain.connect(audioContext.destination);
      testGain.gain.value = 0.001; // Very quiet
      testOsc.frequency.value = 440;
      testOsc.start(audioContext.currentTime);
      testOsc.stop(audioContext.currentTime + 0.1); // Play for 100ms (longer to ensure it plays)
      
      console.log('Test oscillator scheduled');
      
      // 4. Create SEPARATE Audio element just for unlocking (won't be reused)
      const unlockAudio = new Audio();
      unlockAudio.playsInline = true;
      unlockAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
      
      // Start playing to unlock - let it fail silently, we don't need it
      unlockAudio.play();
      
      // Create a SEPARATE Audio element for actual playback
      unlockedAudio = new Audio();
      unlockedAudio.playsInline = true;
      unlockedAudioRef.current = unlockedAudio;
      
      console.log('Audio contexts created and unlock initiated');
      
      // 5. Wait for resume to complete
      await resumePromise;
      
      console.log('AudioContext resumed, state:', audioContext.state);
      clientLogger.audioDebug('audioContext_resumed', {
        state: audioContext.state
      });
      
      // 6. Brief delay to let Audio element unlock (but don't wait for play to complete)
      await new Promise(resolve => setTimeout(resolve, 150));
      
      console.log('Audio fully unlocked', {
        audioContextState: audioContext.state
      });
      
      clientLogger.audioDebug('audio_unlock_complete', {
        audioContextState: audioContext.state
      });
    } catch (unlockError) {
      console.error('Audio unlock failed:', unlockError);
      clientLogger.error('Audio unlock failed', {
        error: unlockError.message,
        errorName: unlockError.name
      });
    }

    try {
      // Brief delay to ensure audio is ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Step 1: Play pager tones FIRST (before calling backend)
      setStatus('playing tones');
      console.log('Starting pager tones...');
      
      try {
        await playPagerTonesSequence(audioContext);
        clientLogger.audioDebug('pager_tones_complete', {});
        console.log('Pager tones complete');
      } catch (pagerError) {
        console.error('Pager tones failed:', pagerError);
        clientLogger.error('Pager tones error', {
          error: pagerError.message,
          errorName: pagerError.name
        });
        toast({
          title: 'Audio Note',
          description: 'Pager tones could not play. Continuing with scenario.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }

      // Step 2: Call server to get dispatcher segment
      const response = await fetch('/scenario/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ runId })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();

      // Update current phase
      if (data.phase) {
        setCurrentPhase(data.phase);
      }

      // Step 2: Show UI (tones already played above)
      setHasStarted(true);
      setIsPlayingOpener(false);
      setStatus('playing dispatcher');

      // Step 4: Play dispatcher segment
      if (data.segments && data.segments.length > 0) {
        const dispatcherSegment = data.segments[0];

        // Add to transcript FIRST (so user sees text immediately)
        // Format with speaker label so parseSpeakers() can extract it correctly
        setTranscript(prev => [...prev, {
          user: '',
          assistant: `**Dispatcher**: ${dispatcherSegment.text}`,
          role: 'dispatcher'
        }]);

        try {
          clientLogger.audioDebug('dispatcher_audio_start', {
            text: dispatcherSegment.text,
            voice: dispatcherSegment.voice
          });
          
          // Fetch the TTS audio
          const ttsResponse = await fetch('/tts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: dispatcherSegment.text,
              voice: dispatcherSegment.voice || 'nova',
              style: 'clean'
            })
          });
          
          if (!ttsResponse.ok) {
            throw new Error(`TTS request failed: ${ttsResponse.status}`);
          }
          
          const audioArrayBuffer = await ttsResponse.arrayBuffer();
          
          // Convert to base64 data URL
          const base64Audio = btoa(
            new Uint8Array(audioArrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          const dataUrl = `data:audio/mpeg;base64,${base64Audio}`;
          
          // Use the single pre-unlocked audio element
          const audio = unlockedAudioRef.current;
          if (audio) {
            // CRITICAL: Set up ALL event listeners BEFORE setting src
            // This ensures we don't miss the 'ended' event on fast/cached audio
            let resolvePlay, rejectPlay;
            const playPromise = new Promise((resolve, reject) => {
              resolvePlay = resolve;
              rejectPlay = reject;
            });
            
            // Attach listeners FIRST
            audio.onended = () => {
              console.log('Dispatcher audio ended');
              resolvePlay();
            };
            
            audio.onerror = (err) => {
              console.error('Dispatcher audio error:', err);
              rejectPlay(err);
            };
            
            // NOW load the audio
            audio.src = dataUrl;
            
            // Start playing
            try {
              await audio.play();
              console.log('Dispatcher audio playing...');
            } catch (playError) {
              console.error('Play failed:', playError);
              rejectPlay(playError);
            }
            
            // Wait for audio to finish
            await playPromise;
            
            clientLogger.audioDebug('dispatcher_audio_complete', {});
          } else {
            console.warn('No unlocked audio element, using fallback');
            await playMp3Bytes(audioArrayBuffer);
          }
        } catch (error) {
          console.error('Failed to play dispatcher audio:', error);
          clientLogger.error('Dispatcher audio playback failed', {
            error: error.message,
            errorName: error.name
          });
          toast({
            title: 'Audio Playback Issue',
            description: 'Could not play dispatcher audio. You can read the text in the transcript.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      }

      // Step 5: Auto-trigger first turn to get preceptor opener
      console.log('Auto-triggering first turn for preceptor opener...');
      setStatus('processing');

      await sendVoiceTurnWithText('', false);

      setStatus('idle');

    } catch (error) {
      console.error('Error playing opener:', error);
      clientLogger.error('playOpenerSequence failed', {
        error: error.message,
        errorName: error.name,
        errorStack: error.stack
      });
      toast({
        title: 'Error Starting Scenario',
        description: error.message || 'Failed to start scenario. Please try again.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      setStatus('idle');
    } finally {
      setIsPlayingOpener(false);
    }
  };

  // Unlock audio on first user interaction (required for mobile)
  const unlockAudio = () => {
    if (audioUnlocked) return;
    
    try {
      // Create and play a silent audio to unlock (required for iOS)
      // This MUST be called synchronously during a user gesture
      const audio = new Audio();
      audio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
      audio.volume = 0;
      
      // Don't await - just trigger play
      audio.play().catch(err => {
        console.warn('Silent audio play failed (expected on desktop):', err);
      });
      
      // Also try to resume AudioContext if it exists
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('AudioContext resume failed:', err);
          });
        }
      } catch (contextErr) {
        // Ignore - AudioContext might not be supported
      }
      
      setAudioUnlocked(true);
      console.log('Audio unlocked for mobile');
    } catch (error) {
      console.warn('Could not unlock audio:', error);
      // Don't throw - allow the app to continue
    }
  };

  // Play audio with mobile-friendly error handling
  const playAudio = async (audioSrc, description = 'audio') => {
    try {
      const audio = new Audio(audioSrc);

      // Set properties for mobile (especially iOS)
      audio.preload = 'auto';
      audio.playsInline = true; // Critical for iOS - prevents fullscreen

      // Wait for audio to be ready
      await new Promise((resolve, reject) => {
        audio.oncanplaythrough = resolve;
        audio.onerror = reject;

        // Timeout after 5 seconds
        setTimeout(() => reject(new Error('Audio load timeout')), 5000);
      });

      // Try to play
      await audio.play();

      // Wait for completion
      await new Promise((resolve) => {
        audio.onended = resolve;
      });

      console.log(`Successfully played ${description}`);
    } catch (error) {
      console.error(`Failed to play ${description}:`, error);
      // Don't throw - continue execution even if audio fails
    }
  };

  const pollForDebrief = async () => {
    try {
      const response = await fetch(`/scenario/get-debrief?runId=${runId}`);
      const data = await response.json();
      
      if (data.ready) {
        setDebriefReady(true);
        setDebriefData(data.debrief);
        
        // Optionally auto-play the debrief audio
        if (data.debriefText) {
          setIsPlayingDebrief(true);
          try {
            await speakText(data.debriefText, data.voice || 'shimmer', 'clean');
          } catch (error) {
            console.error('Failed to play debrief audio:', error);
          }
          setIsPlayingDebrief(false);
        }
        
        return true; // Stop polling
      }
      return false; // Keep polling
    } catch (error) {
      console.error('Error polling for debrief:', error);
      return false;
    }
  };

  const testPagerTones = async () => {
    // CRITICAL: Create and unlock AudioContext SYNCHRONOUSLY in click handler
    let audioContext;
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // SYNCHRONOUSLY call resume - don't await yet
      const resumePromise = audioContext.state === 'suspended' 
        ? audioContext.resume() 
        : Promise.resolve();
      
      console.log('AudioContext created for pager tones, state:', audioContext.state);
      
      // NOW await the resume
      await resumePromise;
      
      console.log('AudioContext resumed, state:', audioContext.state);
      
      setStatus('testing tones');
      
      // Pass the unlocked context to the tone player
      await playPagerTonesSequence(audioContext);
      
      console.log('Pager tones test complete');
      setStatus('idle');
      
      toast({
        title: 'Pager Tones Test',
        description: 'Pager tones played successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Pager tones test failed:', {
        error: error.message,
        name: error.name,
        details: error
      });
      setStatus('idle');
      
      toast({
        title: 'Pager Tones Test Failed',
        description: error.message || 'Could not play pager tones',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      // Clean up the AudioContext
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }
    }
  };

  const handleResumeSession = async () => {
    try {
      onRecoveryModalClose();
      
      if (activeSessionData?.sessionData) {
        // Load session data
        const sessionData = activeSessionData.sessionData;
        
        if (sessionData.conversationId) setConversationId(sessionData.conversationId);
        if (sessionData.conversationTranscript) setTranscript(sessionData.conversationTranscript);
        if (sessionData.currentState?.phase) setCurrentPhase(sessionData.currentState.phase);
        if (sessionData.id) setSessionIdForManualAdvance(sessionData.id);
        
        setHasStarted(true);
        
        toast({
          title: 'Session Resumed',
          description: 'Your previous session has been restored.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to resume session:', error);
      toast({
        title: 'Resume Error',
        description: 'Failed to resume session. Starting fresh.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStartFresh = async () => {
    try {
      onRecoveryModalClose();
      
      // End the active session first
      await endSession({
        voiceSimSessionId: voiceSimSession?.id,
        reason: 'abandoned',
        completedSuccessfully: false
      });
      
      toast({
        title: 'Previous Session Ended',
        description: `Starting fresh. You have ${attemptsRemaining - 1} attempts remaining.`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      
      // Continue with normal initialization (create new session)
      // The initialization logic will run naturally
    } catch (error) {
      console.error('Failed to end previous session:', error);
      toast({
        title: 'Error',
        description: 'Failed to end previous session. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEndSession = async (reason, completedSuccessfully = false) => {
    try {
      onEndModalClose();
      
      // Stop subaudible tone if active
      if (subaudibleToneRef.current) {
        stopSubaudibleTone(subaudibleToneRef.current);
        setSubaudibleToneActive(false);
      }
      
      // End the session
      const result = await endSession({
        voiceSimSessionId: voiceSimSession?.id,
        reason,
        completedSuccessfully
      });
      
      setSessionEnded(true);
      
      toast({
        title: 'Session Ended',
        description: `Session ended successfully. ${result.attemptsRemaining > 0 ? `You have ${result.attemptsRemaining} attempts remaining.` : 'No attempts remaining.'}`,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
      
      // Redirect or reset
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Failed to end session:', error);
      toast({
        title: 'Error',
        description: 'Failed to end session properly.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isInitializing) {
    return (
      <Container maxW="container.lg" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Initializing voice scenario...</Text>
        </VStack>
      </Container>
    );
  }

  if (initError) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Initialization Failed</AlertTitle>
            <AlertDescription>{initError}</AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={8}>
      {/* Recovery Modal */}
      <Modal isOpen={isRecoveryModalOpen} onClose={() => {}} isCentered closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Resume Previous Session?</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                You have an active session in progress. Would you like to resume where you left off, or start fresh?
              </Text>
              <Alert status="warning" fontSize="sm">
                <AlertIcon />
                Starting fresh will count as an abandoned attempt. You have {attemptsRemaining} attempt(s) remaining.
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={handleStartFresh} isLoading={endingSession}>
              Start Fresh
            </Button>
            <Button colorScheme="blue" onClick={handleResumeSession}>
              Resume Session
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* End Session Modal */}
      <Modal isOpen={isEndModalOpen} onClose={onEndModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>End Session</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              {currentPhase === 'END' || sessionEnded ? (
                <>
                  <Text>The scenario is complete. How would you like to proceed?</Text>
                  <Button
                    colorScheme="green"
                    onClick={() => handleEndSession('completed', true)}
                    isLoading={endingSession}
                  >
                    Submit with Evaluation
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleEndSession('completed', false)}
                    isLoading={endingSession}
                  >
                    End Without Evaluation
                  </Button>
                </>
              ) : (
                <>
                  <Alert status="warning">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Are you sure?</AlertTitle>
                      <AlertDescription fontSize="sm">
                        Ending the session now will count as an abandoned attempt.
                        {attemptsRemaining && ` You will have ${attemptsRemaining - 1} attempt(s) remaining.`}
                      </AlertDescription>
                    </Box>
                  </Alert>
                  <Button
                    colorScheme="red"
                    onClick={() => handleEndSession('abandoned', false)}
                    isLoading={endingSession}
                  >
                    Yes, End Session
                  </Button>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onEndModalClose} isDisabled={endingSession}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <VStack spacing={6} align="stretch" minH="calc(100vh - 4rem)">
        {/* Header with Status and End Session button */}
        <HStack justify="space-between">
          {/* Status indicator - only for system admins */}
          {session?.user?.roles?.some(role => 
            role === 'system-admin' || role?.key === 'system-admin'
          ) && (
            <HStack>
              <Text>Status:</Text>
              <Badge colorScheme={status === 'idle' ? 'green' : 'blue'}>
                {status}
              </Badge>
              {(status === 'processing' || isPlayingOpener) && <Spinner size="sm" />}
            </HStack>
          )}
          
          {/* End Session button - visible once scenario starts */}
          {hasStarted && !sessionEnded && (
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              onClick={onEndModalOpen}
              leftIcon={<Text>⏹️</Text>}
            >
              End Session
            </Button>
          )}
        </HStack>
        
        {/* Show loading animation while playing opener */}
        {isPlayingOpener && (
          <VStack spacing={4} py={12} flex={1} justify="center">
            <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
            <Heading size="lg" color="blue.400">Standby for dispatch...</Heading>
            <Text color="gray.400" fontSize="sm">Preparing your scenario</Text>
          </VStack>
        )}
        
        {/* Start button (before scenario starts) */}
        {!hasStarted && !isPlayingOpener && (
          <VStack spacing={4} flex={1} justify="center">
            {/* Volume control for dispatch tones */}
            <Box w="100%" maxW="400px">
              <HStack>
                <Text fontSize="sm" minW="120px">Dispatch Tones:</Text>
                <Slider
                  aria-label="dispatch-tone-volume"
                  value={pagerToneVolume * 100}
                  onChange={(val) => setPagerToneVolume(val / 100)}
                  min={0}
                  max={100}
                  step={5}
                >
                  <SliderTrack bg="gray.600">
                    <SliderFilledTrack bg="blue.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text fontSize="sm" minW="45px" textAlign="right">
                  {Math.round(pagerToneVolume * 100)}%
                </Text>
              </HStack>
            </Box>

            {/* Speech speed control */}
            <Box w="100%" maxW="400px">
              <HStack>
                <Text fontSize="sm" minW="120px">Speech Speed:</Text>
                <Slider
                  aria-label="speech-speed"
                  value={speechSpeed}
                  onChange={(val) => setSpeechSpeed(val)}
                  min={-0.5}
                  max={0.5}
                  step={0.25}
                >
                  <SliderTrack bg="gray.600">
                    <SliderFilledTrack bg="green.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text fontSize="sm" minW="45px" textAlign="right">
                  {speechSpeed >= 0 ? '+' : ''}{speechSpeed.toFixed(2)}x
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={1}>
                -0.50x (slowest) | 0.00x (normal) | +0.50x (fastest)
              </Text>
            </Box>

            <HStack spacing={4} w="100%">
              <Button
                size="lg"
                colorScheme="green"
                onClick={async () => {
                  // Set loading states IMMEDIATELY in the click handler (synchronous)
                  setIsPlayingOpener(true);
                  setStatus('playing opener');
                  
                  // Then call the async function and catch any errors
                  try {
                    await playOpenerSequence();
                  } catch (error) {
                    console.error('Failed to start scenario:', error);
                    setIsPlayingOpener(false);
                    setStatus('idle');
                    toast({
                      title: 'Error Starting Scenario',
                      description: error.message || 'Failed to start scenario. Please try again.',
                      status: 'error',
                      duration: 8000,
                      isClosable: true,
                    });
                  }
                }}
                isDisabled={isPlayingOpener || status === 'testing tones'}
                isLoading={isPlayingOpener}
                loadingText="Starting scenario..."
                flex={1}
              >
                🚨 Start Scenario
              </Button>
              {/* Only show test button for system-admin users */}
              {(session?.user?.roles?.some(role =>
                role === 'system-admin' || role?.key === 'system-admin'
              )) && (
                <Button
                  size="lg"
                  variant="outline"
                  colorScheme="blue"
                  onClick={testPagerTones}
                  isDisabled={isPlayingOpener || status === 'testing tones'}
                  isLoading={status === 'testing tones'}
                  loadingText="Playing..."
                >
                  🔊 Test Pager Tones
                </Button>
              )}
            </HStack>
          </VStack>
        )}
        
        {/* Main content area (after scenario starts) */}
        {hasStarted && (
          <>
            {/* Audio player - hidden but functional */}
            <audio ref={audioRef} style={{ display: 'none' }} />

            {/* Phase tracker */}
            <VoiceSimulationFlowChart currentPhase={currentPhase} />

            {/* Phase transition indicator */}
            {phaseTransition && (
              <ScaleFade initialScale={0.8} in={true}>
                <Alert status="success" borderRadius="md" bg="green.900" borderColor="green.500" borderWidth="2px">
                  <AlertIcon />
                  <Box flex={1}>
                    <AlertTitle fontSize="md">Phase Transition</AlertTitle>
                    <AlertDescription fontSize="sm">
                      Now entering: <strong>{phaseTransition}</strong>
                    </AlertDescription>
                  </Box>
                </Alert>
              </ScaleFade>
            )}

            {/* Instruction banner */}
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Speaking Instructions</AlertTitle>
                <AlertDescription fontSize="xs">
                  Always specify WHO you're speaking to (e.g., "Ask the patient...", "Tell the nurse...", "Partner, can you...").
                  If you don't specify, the system will ask you to clarify.
                </AlertDescription>
              </Box>
            </Alert>

            {/* Manual phase advance button (for admins) */}
            {session?.user?.roles?.some(role =>
              role === 'system-admin' || role?.key === 'system-admin'
            ) && sessionIdForManualAdvance && (
              <ManualPhaseAdvanceButton
                sessionId={sessionIdForManualAdvance}
                currentPhase={currentPhase}
                onPhaseChanged={(newPhase) => setCurrentPhase(newPhase)}
              />
            )}

            {/* Transcript - scrollable dialogue only */}
            <Box
              border="1px"
              borderColor="gray.600"
              borderRadius="md"
              flex={1}
              display="flex"
              flexDirection="column"
              bg="gray.800"
              minH="300px"
              maxH="600px"
              overflow="hidden"
            >
              <Box
                flex={1}
                overflowY="auto"
                p={4}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#2D3748',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#4A5568',
                    borderRadius: '4px',
                  },
                }}
              >
                <VStack align="stretch" spacing={4}>
                  {transcript.map((turn, idx) => (
                    <Fade in={true} key={idx}>
                      <Box>
                        {/* User message - aligned right */}
                        {turn.user && (
                          <HStack justify="flex-end" mb={3} spacing={3}>
                            <Box
                              bg="blue.600"
                              borderRadius="lg"
                              p={3}
                              maxW="80%"
                              borderBottomRightRadius="sm"
                            >
                              <Badge colorScheme="blue" mb={1}>You</Badge>
                              <Text whiteSpace="pre-wrap" fontSize="sm">{turn.user}</Text>
                            </Box>
                            <Avatar size="sm" name="You" bg="blue.500" color="white">
                              🙋
                            </Avatar>
                          </HStack>
                        )}

                        {/* Assistant message(s) - aligned left, may have multiple speakers */}
                        {turn.assistant && (
                          <VStack align="stretch" spacing={2}>
                            {parseSpeakers(turn.assistant).map((speakerMsg, speakerIdx) => {
                              const badge = getSpeakerBadge(speakerMsg.speaker);
                              return (
                                <HStack key={speakerIdx} align="start" spacing={3} justify="flex-start">
                                  <Avatar size="sm" bg={`${badge.color}.500`} color="white">
                                    {badge.emoji}
                                  </Avatar>
                                  <Box
                                    bg="gray.700"
                                    borderRadius="lg"
                                    p={3}
                                    maxW="80%"
                                    borderBottomLeftRadius="sm"
                                  >
                                    <Badge colorScheme={badge.color} mb={1}>
                                      {badge.label}
                                    </Badge>
                                    <Text whiteSpace="pre-wrap" fontSize="sm">{speakerMsg.text}</Text>
                                  </Box>
                                </HStack>
                              );
                            })}
                          </VStack>
                        )}
                        {idx < transcript.length - 1 && <Divider mt={3} opacity={0.3} />}
                      </Box>
                    </Fade>
                  ))}

                  {/* Loading indicator */}
                  {status === 'processing' && (
                    <HStack align="center" spacing={3} opacity={0.6} justify="flex-start">
                      <Spinner size="sm" />
                      <Text fontSize="sm" fontStyle="italic">Processing...</Text>
                    </HStack>
                  )}

                  <div ref={transcriptEndRef} />
                </VStack>
              </Box>
            </Box>
            
            {/* Debrief section - shows when debrief is ready */}
            {debriefReady && debriefData && (
              <Box
                border="1px"
                borderColor="green.500"
                borderRadius="md"
                p={4}
                bg="gray.800"
              >
                <Heading size="md" mb={3} color="green.400">Scenario Debrief</Heading>

                {isPlayingDebrief && (
                  <HStack mb={3}>
                    <Spinner size="sm" color="green.400" />
                    <Text color="gray.400">Playing debrief audio...</Text>
                  </HStack>
                )}

                <VStack align="stretch" spacing={4}>
                  {/* Overall Assessment */}
                  <Box>
                    <Text fontWeight="bold" color="blue.300">Overall:</Text>
                    <Text>{debriefData.narrative?.overall}</Text>
                  </Box>

                  {/* Phase Timeline */}
                  {debriefData.phaseSummary?.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" color="cyan.300" mb={1}>Phase Timeline:</Text>
                      <VStack align="stretch" spacing={1} pl={2}>
                        {debriefData.phaseSummary.map((p, i) => (
                          <HStack key={i} align="start" spacing={2}>
                            <Badge colorScheme="cyan" fontSize="xs" minW="80px" textAlign="center">{p.phase}</Badge>
                            <Text fontSize="sm">{p.summary}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {/* Intervention Checklist */}
                  {debriefData.interventionChecklist?.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" color="orange.300" mb={1}>
                        Intervention Checklist ({debriefData.interventionChecklist.filter(i => i.status === 'performed').length}/{debriefData.interventionChecklist.length}):
                      </Text>
                      <VStack align="stretch" spacing={1} pl={2}>
                        {debriefData.interventionChecklist.map((item, i) => {
                          const statusColor = {
                            performed: 'green',
                            missed: 'red',
                            partial: 'yellow',
                            unclear: 'gray'
                          }[item.status] || 'gray';
                          return (
                            <HStack key={i} align="start" spacing={2}>
                              <Badge colorScheme={statusColor} fontSize="xs" minW="70px" textAlign="center">
                                {item.status}
                              </Badge>
                              <Badge colorScheme={item.priority === 'critical' ? 'red' : item.priority === 'secondary' ? 'orange' : 'gray'} fontSize="xs" variant="outline">
                                {item.priority}
                              </Badge>
                              <Box>
                                <Text fontSize="sm" fontWeight="medium">{item.action}</Text>
                                {item.evidence && (
                                  <Text fontSize="xs" color="gray.400" fontStyle="italic">{item.evidence}</Text>
                                )}
                              </Box>
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {/* What Went Well */}
                  <Box>
                    <Text fontWeight="bold" color="green.300">What Went Well:</Text>
                    <VStack align="stretch" pl={4}>
                      {(debriefData.narrative?.whatWentWell || []).map((item, i) => (
                        <Text key={i}>• {item}</Text>
                      ))}
                    </VStack>
                  </Box>

                  {/* Areas to Improve */}
                  <Box>
                    <Text fontWeight="bold" color="yellow.300">Areas to Improve:</Text>
                    <VStack align="stretch" pl={4}>
                      {(debriefData.narrative?.topImprovements || []).map((item, i) => (
                        <Text key={i}>• {item}</Text>
                      ))}
                    </VStack>
                  </Box>

                  {/* Missed Critical Actions */}
                  {debriefData.narrative?.missedCriticalActions?.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" color="red.300">Missed Critical Actions:</Text>
                      <VStack align="stretch" pl={4}>
                        {debriefData.narrative.missedCriticalActions.map((item, i) => (
                          <Text key={i}>• {item}</Text>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {/* Competency Levels */}
                  <Box>
                    <Text fontWeight="bold" color="purple.300">Competency Levels:</Text>
                    <VStack align="stretch" pl={4}>
                      <Text>Scene Management: {debriefData.competencies?.sceneManagement || 'N/A'}</Text>
                      <Text>Assessment: {debriefData.competencies?.assessment || 'N/A'}</Text>
                      <Text>Treatment: {debriefData.competencies?.treatment || 'N/A'}</Text>
                      <Text>Communication: {debriefData.competencies?.communication || 'N/A'}</Text>
                    </VStack>
                  </Box>

                  {/* Recommended Focus Areas */}
                  {debriefData.recommendedFocusAreas?.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" color="teal.300">Recommended Focus Areas:</Text>
                      <HStack spacing={2} pl={4} flexWrap="wrap">
                        {debriefData.recommendedFocusAreas.map((area, i) => (
                          <Badge key={i} colorScheme="teal" variant="subtle">{area}</Badge>
                        ))}
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}
            
            {/* Mode toggle */}
            <HStack justify="center" py={2}>
              <Button
                size="sm"
                variant={inputMode === 'voice' ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={() => setInputMode('voice')}
              >
                🎤 Voice
              </Button>
              <Button
                size="sm"
                variant={inputMode === 'text' ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={() => setInputMode('text')}
              >
                ⌨️ Text
              </Button>
            </HStack>
            
            {/* Instructions */}
            <Text fontSize="sm" color="gray.400" textAlign="center">
              {inputMode === 'voice' ? (
                'Hold the button or press and hold SPACEBAR to record your response. Release to send.'
              ) : (
                'Type your response and press Enter or click Send.'
              )}
            </Text>
            
            {/* Input controls - FIXED AT BOTTOM */}
            <Box pb={4}>
              {/* Voice input */}
              {inputMode === 'voice' && (
                <Button
                  size="lg"
                  w="100%"
                  h="70px"
                  colorScheme={isRecording ? 'red' : 'blue'}
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    startRecording();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    stopRecording();
                  }}
                  isDisabled={status !== 'idle' && !isRecording}
                  fontSize="xl"
                >
                  {isRecording ? '🔴 Recording... (Release to send)' : '🎤 Push to Talk'}
                </Button>
              )}
              
              {/* Text input */}
              {inputMode === 'text' && (
                <VStack spacing={2} align="stretch">
                  <Textarea
                    ref={textInputRef}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendTextTurn();
                      }
                    }}
                    placeholder="Type your response... (Shift+Enter for new line, Enter to send)"
                    isDisabled={status !== 'idle'}
                    minH="100px"
                    resize="vertical"
                    fontSize="md"
                  />
                  <Button
                    size="lg"
                    colorScheme="green"
                    onClick={sendTextTurn}
                    isDisabled={status !== 'idle' || !textInput.trim()}
                    isLoading={status === 'uploading' || status === 'processing'}
                    width="full"
                  >
                    {status === 'processing' ? 'Processing...' : 'Send'}
                  </Button>
                </VStack>
              )}
            </Box>
          </>
        )}
        
        {/* Debug info - only for system admins */}
        {session?.user?.roles?.some(role => 
          role === 'system-admin' || role?.key === 'system-admin'
        ) && (
          <Box fontSize="xs" color="gray.500">
            <Text>Run ID: {runId}</Text>
            <Text>Conversation ID: {conversationId || '(none yet)'}</Text>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

VoiceScenarioRunner.propTypes = {
  scenarioId: PropTypes.string.isRequired,
  voiceSimSession: PropTypes.object,
  onComplete: PropTypes.func
};

export default VoiceScenarioRunner;
